import { type FC, useState, useCallback, type KeyboardEvent, type FocusEvent } from 'react';
import classnames from 'classnames';
import { FormattedMessage } from 'dibs-react-intl';
import { Link } from 'dibs-elements/exports/Link.tsx';
import { Tooltip } from 'dibs-elements/exports/Tooltip.tsx';
import Flag from 'dibs-icons/exports/legacy/Flag.ts';
import MoreDots from 'dibs-icons/exports/legacy/MoreDots.ts';
import { NftPublicProfileReportModal } from './NftPublicProfileReportModal.tsx';

import styles from './NftPublicProfileReportTooltip.module.css';

const TOOLTIP_ID = 'public-profile-report-tooltip';
const TRIGGER_ID = 'public-profile-report-trigger';

export const NftPublicProfileReportTooltip: FC<{
    onOpen?: () => void;
    username: string;
    linkClass?: string;
}> = ({ onOpen, linkClass, username }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [mouseDown, setMouseDown] = useState(false);
    const handleOpen = useCallback(() => {
        onOpen?.();
        setIsOpen(true);
    }, [onOpen]);
    const handleClose = useCallback(() => setIsOpen(false), []);
    const handleBlur = useCallback((e: FocusEvent) => {
        if (!e.currentTarget.contains(e.relatedTarget)) {
            setIsOpen(false);
        }
    }, []);
    function handleTooltipEsc(e: KeyboardEvent): void {
        if (e.key === 'Escape') {
            setIsOpen(false);
        }
    }

    return (
        <div
            role="button"
            tabIndex={0}
            onBlur={handleBlur}
            onMouseDown={() => setMouseDown(true)}
            onMouseUp={() => setMouseDown(false)}
            onMouseLeave={() => setMouseDown(false)}
            className={styles.socialTooltip}
        >
            <Link
                className={classnames(styles.socialLink, linkClass)}
                ariaDescribedBy={TOOLTIP_ID}
                id={TRIGGER_ID}
                onClick={e => {
                    e.preventDefault();
                    handleOpen();
                }}
                onFocus={handleOpen}
                onKeyDown={handleTooltipEsc}
            >
                <MoreDots className={styles.socialIcon} />
            </Link>
            <Tooltip
                id={TOOLTIP_ID}
                direction="top"
                isVisible={isOpen}
                keepInViewport
                paddingSize="small"
                align="center"
                closeOnOutsideClick={!mouseDown}
                onClose={handleClose}
            >
                <div className={styles.reportWrapper}>
                    <Link buyerLinkType="linkNoDecoration" onClick={() => setModalIsOpen(true)}>
                        <Flag className={styles.flagIcon} />
                        <FormattedMessage
                            id="nft.pdp.creator.reportProfile"
                            defaultMessage="Report Profile"
                        />
                    </Link>
                </div>
            </Tooltip>
            <NftPublicProfileReportModal
                username={username}
                isOpen={modalIsOpen}
                onClose={() => setModalIsOpen(false)}
            />
        </div>
    );
};
